import { IconButton, Tooltip } from "@mui/material";
import { useLang, useT } from "src/i18n";
import { useAuth } from "src/context/AuthContext";
import { saveLanguagePreference } from "src/utils/languageApi";

// Both flags are drawn into the SAME 24x16 box with preserveAspectRatio="none" so
// toggling can never shift the toolbar layout. The sub-10% distortion off each
// flag's official ratio (FI 18:11, UK 2:1) is invisible at this size and is what
// every normalised flag-icon set does. Flag emoji are NOT an option: Windows Chrome
// has no regional-indicator glyphs and renders 🇫🇮 as the literal letters "FI".
const BOX = { width: 24, height: 16, display: "block" };

// Finland: white field, blue cross — official bands are 4:3:4 vertical, 5:3:10 horizontal.
const FlagFi = () => (
    <svg viewBox="0 0 18 11" preserveAspectRatio="none" style={BOX} aria-hidden="true" focusable="false">
        <rect width="18" height="11" fill="#fff" />
        <rect y="4" width="18" height="3" fill="#003580" />
        <rect x="5" width="3" height="11" fill="#003580" />
    </svg>
);

// United Kingdom: Union Jack. The clipPath is the counterchange that keeps each red
// diagonal on the correct side of its quadrant; the id is bj-prefixed because SVG
// ids are document-global. No outer clip needed — the root <svg> clips at its viewBox.
const FlagEn = () => (
    <svg viewBox="0 0 60 30" preserveAspectRatio="none" style={BOX} aria-hidden="true" focusable="false">
        <clipPath id="bj-uj-clip">
            <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
        </clipPath>
        <rect width="60" height="30" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
        <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#bj-uj-clip)" stroke="#C8102E" strokeWidth="4" />
        <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
    </svg>
);

// One button showing the flag of the ACTIVE language; clicking it swaps to the other.
// Persists to localStorage always, and to personSettings when signed in — the
// latter is what makes provider lifecycle emails arrive in the right language.
export default function LanguageSwitcher() {
    const { lang, setLang } = useLang();
    const { token } = useAuth();
    const t = useT();
    const label = t("common.languageToggle");

    const toggle = () => {
        const next = lang === "fi" ? "en" : "fi";
        setLang(next);
        if (token) saveLanguagePreference({ token, language: next });
    };

    return (
        <Tooltip title={label}>
            <IconButton
                size="small"
                onClick={toggle}
                aria-label={label}
                sx={{
                    mr: 1,
                    p: 0.5,
                    // The white Finnish field needs a hairline so it doesn't bleed into
                    // the teal AppBar. `divider` is the theme token — no hardcoded hex.
                    "& svg": { border: 1, borderColor: "divider", borderRadius: "2px" },
                }}
            >
                {lang === "en" ? <FlagEn /> : <FlagFi />}
            </IconButton>
        </Tooltip>
    );
}
